<script lang="ts" setup>
import type { Address } from 'viem';

import { computed, ref, storeToRefs, useAppConfig, useAsyncData, useProviderConfig, useUserStore } from '#imports';

const props = defineProps<{ owner: Address }>();

type ParamsType =
    Pick<AppPagination, 'page' | 'size'> &
    {
        direction: ApiDirection;
        network: User['network'];
        search: string;
    };

const isOpenFilter = ref(true);
const changeGrid = ref<boolean>(false);

const {
    filter: {
        networks: filterNetworksList
    }
} = useAppConfig();

const {
    user,
    network
} = storeToRefs(useUserStore());

const params = ref<ParamsType>({
    page: 1,
    size: 10,
    direction: 'asc',
    network: network.value
        ? network.value
        : 'eth',
    search: ''
});

const {
    nft,
    QueryWTFApiKey
} = useWTFApi();

const onChangeQueryParams = (values: Partial<ParamsType>) => {
    params.value = { ...params.value, ...values };
};

const { map: { chainMap } } = useProviderConfig()(['map']);

const {
    data,
    status,
    refresh,
    error
} = useAsyncData(
    `${QueryWTFApiKey.NFT_BY_ADDRESS}-${props.owner}`,
    async () => {
        const { data } = await nft.getNftByWallet(
            props.owner,
            {
                ...params.value
            // tab: query.value.tab
            }
        );
        return data;
    },
    {
        watch: [params]
    }
);

const networkName = computed(() => params.value.network ? chainMap[params.value.network].name : null);

</script>

<template>
  <div class="display-flex justify-start width-full">
    <FiltersPanel v-model:model-value="isOpenFilter">
      <div class="display-flex blocks flex-column pad-24 gap-12">
        <p class="compact-500-accent">
          Blockchain
        </p>

        <ToggleIconGroup :items="filterNetworksList"
                         :model-value="params.network"
                         @update:model-value="(val: User['network'] ) => onChangeQueryParams({ network: val, page: 1, size: 10 })"
        />
      </div>
    </FiltersPanel>

    <div class="display-flex grow flex-column width-full gap-16 justify-between">
      <div class="gap-16 display-flex flex-column grow">
        <SearchBar v-model:change-grid="changeGrid"
                   v-model:model-value="isOpenFilter"
                   :selected-direction="params.direction"
                   type-view="collection"
                   @on-change-direction="(val: ApiDirection) => onChangeQueryParams({ direction: val })"
                   @on-change-search="(val:string) => onChangeQueryParams({ search: val, page: 1 })"
        />

        <GridLayout :change-grid="changeGrid"
                    :data="data"
                    :is-error="status === 'error'"
                    :is-loading="status === 'pending'"
                    :loader-size="10"
        >
          <template #loader>
            <LoaderSkeleton class="nft-skeleton" />
          </template>
          <template #cart="{ item }">
            <Suspense>
              <template #fallback>
                <LoaderSkeleton class="nft-skeleton" />
              </template>
              <LazyNftCard :key="item.uuid"
                           :identifier="item.identifier"
                           :itemType="item.itemType"
                           :img="item.displayAnimationURL || item.displayImageURL"
                           :network="item.network"
                           :price="item.price"
                           :ownedCount="item.ownedCount"
                           :collectionSlug="item.collectionSlug"
                           :title="item.name || item.identifier"
                           :uuid="item.uuid"
                           sizes="286px xs:254px sm:282px md:219px lg:181px xl:215px xxl:321px"
                           type="profile"
              />
            </Suspense>
          </template>
          <template #empty>
            <div class="display-flex align-items-center grow flex-column justify-center"
            >

              <p v-if="networkName"
                 class="text-center mb-8"
                 style="max-width: 400px"
              >You have no tokens on the {{ networkName }} network, try&nbsp;again or&nbsp;switch networks</p>
              <Button :size="'size.medium'"
                      :color="'bg.mojito'"
              >
                <button class="compact-500-accent"
                        type="button"
                        @click="refresh()"
                >
                  Refetch
                </button>
              </Button>
            </div>
          </template>
          <template #error>
            <span>{{ error?.message }}</span>
          </template>
        </GridLayout>
        <Suspense>
          <LazyPagination :page="+params.page"
                          :size="+params.size"
                          :total="10"
                          :visible-items="3"
                          @on-page="(page: number) => onChangeQueryParams({ page })"
          />
          <template #fallback>
            <div class="display-flex flex-row align-items-center paginate gap-8">
              <LoaderSkeleton :height="30"
                              :width="30" />
              <LoaderSkeleton :height="30"
                              :width="30" />
              <LoaderSkeleton :height="30"
                              :width="30" />
            </div>
          </template>
        </Suspense>
      </div>
    </div>
  </div>
</template>
